import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, GlassCard, GhostButton } from "@/components/Primitives";
import { Sparkles, Mail, Shield, Database, LogOut } from "lucide-react";
import WebhooksSection from "@/components/WebhooksSection";
import ApiKeysSection from "@/components/ApiKeysSection";

export default function Settings() {
  const { user, logout } = useAuth();
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Workspace, account, and platform configuration." testId="settings-header" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <GlassCard testId="settings-account">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#0050C8] flex items-center justify-center text-lg font-semibold glow-blue">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div>
              <div className="font-display text-xl text-white">{user?.name || "Admin"}</div>
              <div className="text-xs text-white/45">{user?.email}</div>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between py-2 border-b border-white/[0.05]">
              <span className="text-white/55 flex items-center gap-2"><Mail className="w-4 h-4" /> Email</span>
              <span className="text-white">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-white/[0.05]">
              <span className="text-white/55 flex items-center gap-2"><Shield className="w-4 h-4" /> Role</span>
              <span className="text-white capitalize">{user?.role}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-white/55 flex items-center gap-2"><Database className="w-4 h-4" /> Workspace</span>
              <span className="text-white">Equinox Systems</span>
            </div>
          </div>
          <div className="mt-6">
            <GhostButton onClick={logout} data-testid="settings-logout"><LogOut className="w-4 h-4" /> Sign out</GhostButton>
          </div>
        </GlassCard>

        <GlassCard testId="settings-future">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-white/[0.05] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#007AFF]" />
            </div>
            <div className="font-display text-xl">Architected for tomorrow</div>
          </div>
          <p className="text-white/55 text-sm leading-relaxed">
            Equinox OS is built role-aware from day one. Adding <b>Staff</b>, <b>Project Manager</b>, and a
            <b> Client Portal</b> requires no schema redesign — every client, project, hosting, and maintenance
            record is already linked through stable IDs.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            {["Staff role", "Project Manager", "Client portal", "Contracts", "Support requests", "AI proposals"].map((f) => (
              <div key={f} className="flex items-center gap-2 text-white/45">
                <div className="w-1 h-1 rounded-full bg-[#007AFF]" /> {f}
              </div>
            ))}
          </div>
        </GlassCard>

        <WebhooksSection />

        <ApiKeysSection />

        <GlassCard className="lg:col-span-2" testId="settings-platform">
          <div className="font-display text-xl mb-4">Platform</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Version</div><div>1.1</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Database</div><div>MongoDB</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Storage</div><div>Emergent Object Storage</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Auth</div><div>JWT</div></div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
